import { useEffect, useState } from "react";
import { query } from "./dataClient.js";

// useCompositionFlows — co-occurrence between adjacent composition columns, for
// the sankey overlay. A flow A_member → B_member is |scope ∩ A_member ∩ B_member|.
//
// Fetched the SAME way the scope columns fetch their flow links: one batched
// co-occurrence query per adjacent column pair (getFlowLinks → runAnalyticsFlowPair
// groups PROMPT by (a, b) with both id-lists as IN filters, counting each pair in
// one query) rather than one re-scope getDimensionEntries per source member. All
// pairs share the anatomy scope, so a single getFlowLinks call fans them in
// parallel. Aborts on scope/columns change.
//
// computeCompositionFlows is pure aside from the injected query, unit-tested in
// useCompositionFlows.test.js.

/**
 * @param {{scopePath:Array, columns:Array<{dimKey:string, ids:string[]}>, range:*, query:Function, signal?:{cancelled:boolean}}} args
 * @returns {Promise<Array<{pair:number, aRow:number, bRow:number, count:number}>>} flows keyed by row index within each column
 */
export async function computeCompositionFlows({ scopePath, columns, range, query: q, signal }) {
  const cols = columns || [];
  if (cols.length < 2) return [];
  const pairs = cols.slice(0, -1).map((colA, i) => ({
    aDim: colA.dimKey, aIds: colA.ids,
    bDim: cols[i + 1].dimKey, bIds: cols[i + 1].ids,
  }));
  const res = await q(
    "getFlowLinks",
    { scopePath, pairs, range, scoped: true },
    { cache: true },
  ).catch((e) => { console.warn("[useCompositionFlows] flow fetch failed", e); return null; });
  if (signal?.cancelled || !res) return [];

  const out = [];
  (res.pairs || []).forEach((pr, pair) => {
    const colA = cols[pair];
    const colB = cols[pair + 1];
    if (!colA || !colB) return;
    const aRowById = new Map(colA.ids.map((id, r) => [String(id), r]));
    const bRowById = new Map(colB.ids.map((id, r) => [String(id), r]));
    for (const link of pr?.links || []) {
      const aRow = aRowById.get(String(link.aId));
      const bRow = bRowById.get(String(link.bId));
      // Only links whose BOTH endpoints are shown in their column keep a ribbon.
      if (aRow != null && bRow != null) out.push({ pair, aRow, bRow, count: Math.max(0, Number(link.count) || 0) });
    }
  });
  return out;
}

/**
 * @param {Array} scopePath
 * @param {Array<{dimKey, ids: string[]}>} columns  shown columns in display order (top ids per column)
 * @param {*} range
 * @returns {Array<{pair, aRow, bRow, count}>} flows keyed by row index within each column
 */
export function useCompositionFlows(scopePath, columns, range) {
  const [flows, setFlows] = useState([]);
  const sig = JSON.stringify([scopePath, (columns || []).map((c) => [c.dimKey, c.ids])]);

  useEffect(() => {
    if ((columns || []).length < 2) { setFlows([]); return undefined; }
    const signal = { cancelled: false };
    computeCompositionFlows({ scopePath, columns, range, query, signal })
      .then((out) => { if (!signal.cancelled) setFlows(out); })
      .catch((e) => { if (!signal.cancelled) { console.error("[useCompositionFlows]", e); setFlows([]); } });
    return () => { signal.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, range]);

  return flows;
}
